import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useAPIClient } from '@/hooks/useAPIClient';
import { DocumentListResponse, DocumentViewResponse, DocumentStatusResponse, ConsistencyCheckResponse } from '@/types/api';

export function useDocuments(): UseQueryResult<DocumentListResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['documents'],
    queryFn: () => apiClient.documents.list(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });
}

export function useDocumentView(filename: string | null): UseQueryResult<DocumentViewResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['document-view', filename],
    queryFn: () => apiClient.documents.view(filename!),
    enabled: !!filename,
    staleTime: 10 * 60 * 1000, // 10 minutes (documents don't change often)
    retry: 2,
  });
}

export function useDocumentStatus(): UseQueryResult<DocumentStatusResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['document-status'],
    queryFn: () => apiClient.documents.status(),
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 3,
  });
}

export function useConsistencyCheck(): UseQueryResult<ConsistencyCheckResponse, Error> {
  const apiClient = useAPIClient();

  return useQuery({
    queryKey: ['document-consistency'],
    queryFn: () => apiClient.documents.checkConsistency(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: false, // Manual trigger only
    retry: 2,
  });
}